/*
 * Figma `46:67` — Top Bar: the drawer toggle, the workspace context, and global search.
 *
 * The context line is the answer to "where am I" that the page title cannot give: the title says
 * which screen, this says which event's data you are about to change. On a platform console that
 * distinction is the difference between editing one organizer's settings and everyone's.
 *
 * Search is a form, so Enter submits it, and it searches the console rather than filtering the
 * table below — the table has its own search in its own toolbar, and one box that sometimes meant
 * one and sometimes the other would be worse than two boxes that each mean one thing.
 */
import { useState } from 'react';
import type { ReactNode } from 'react';
import { Button, SearchField } from '../ui';

export function TopBar({ context, contextDetail, onOpenSidebar, onSearch, searchLabel, actions }: {
  /** e.g. the event name. */
  context: string;
  /** e.g. the sale status, or the environment. */
  contextDetail?: ReactNode;
  onOpenSidebar: () => void;
  /** Omitted where the console has nothing to search yet — the box is then not drawn at all. */
  onSearch?: (query: string) => void;
  searchLabel?: string;
  actions?: ReactNode;
}) {
  const [query, setQuery] = useState('');

  return (
    <header className="jt-topbar">
      <Button
        className="jt-drawer-toggle"
        variant="ghost"
        size="sm"
        iconOnly
        aria-label="เปิดเมนู"
        onClick={onOpenSidebar}
        icon={
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden="true">
            <path d="M4 7h16M4 12h16M4 17h16" />
          </svg>
        }
      />
      <div className="jt-topbar-context">
        <strong title={context}>{context}</strong>
        {contextDetail}
      </div>
      <div className="jt-topbar-spacer" />
      {onSearch && (
        <form
          className="jt-topbar-search"
          onSubmit={(e) => { e.preventDefault(); onSearch(query); }}
          role="search"
        >
          <SearchField
            value={query}
            onChange={setQuery}
            label={searchLabel ?? 'ค้นหาในคอนโซล'}
            placeholder={searchLabel ?? 'ค้นหา…'}
          />
        </form>
      )}
      {actions}
    </header>
  );
}
